import { FloatingChatConfig, EventType, Position, Theme } from './types';
import { ConfigManager } from './config';
import { FloatingButton } from './FloatingButton';
import { ChatWindow } from './ChatWindow';
import { ChatModal } from './ChatModal';
import { EventManager } from './EventManager';
import { StorageManager } from './StorageManager';
import { injectStyles, removeStyles } from './utils';
import { WIDGET_STYLES } from './styles';

import { sendMessageToAgentLocal } from '../services/chatApi';
import { Agent } from '../types';
import { loginByCode, setExternalVariables, sendMessageToAgent, createSession } from './api';
import { tokenManager } from '../utils/useToken';

const STYLE_ID = 'floating-chat-widget-styles';

class FloatingChatWidget {
  private configManager: ConfigManager | null = null;
  private button: FloatingButton | null = null;
  private window: ChatWindow | null = null;
  private eventManager: EventManager | null = null;
  private storageManager: StorageManager | null = null;
  private isInitialized: boolean = false;
  private container: HTMLDivElement | null = null;
  private isAuthenticated: boolean = false; // 认证状态标记

  private handleDocumentClick = (event: MouseEvent): void => {
    if (
      this.isInitialized &&
      this.window?.isOpen() &&
      this.container &&
      !this.container.contains(event.target as Node)
    ) {
      this.close();
    }
  };

  /**
   * 处理免密登录
   */
  private async authenticate(loginInfo?: any): Promise<boolean> {
    if (!this.configManager) {
      console.error('FloatingChat: ConfigManager not initialized');
      return false;
    }

    // 如果已经认证过，直接返回成功
    if (this.isAuthenticated) {
      console.log('FloatingChat: Already authenticated');
      return true;
    }

    const config = this.configManager.getConfig();
    
    // 如果已经有 token，标记为已认证
    if (config.token) {
      this.isAuthenticated = true;
      return true;
    }

    // 使用传入的 loginInfo 或配置中的 loginInfo
    const authInfo = loginInfo || config.loginInfo;
    
    if (!authInfo) {
      console.warn('FloatingChat: No loginInfo provided');
      return false;
    }
    
    try {
      let loginData: any = authInfo;
      if (typeof authInfo === 'string') {
        try {
          loginData = JSON.parse(authInfo);
        } catch (e) {
          console.error('FloatingChat: Failed to parse loginInfo JSON', e);
          return false;
        }
      }

      // 验证必需字段
      if (!loginData || !loginData.code || !loginData.origin) {
        console.warn('FloatingChat: loginInfo missing required fields (code, origin)');
        return false;
      }

      console.log('FloatingChat: Starting authentication, code:', loginData.code);
      
      const baseUrl = config.baseUrl || '';
      const loginResult = await loginByCode(baseUrl, {
        code: loginData.code,
        origin: loginData.origin
      });
      
      if (loginResult.success && loginResult.token) {
        // 更新token
        tokenManager.setToken(loginResult.token);
        this.configManager.updateConfig({ token: loginResult.token });
        this.isAuthenticated = true;
        console.log('FloatingChat: Authentication successful');
        
        // 认证成功后设置参数
        if (config.parameters) {
          await this.setParameters(config.parameters);
        }
        
        return true;
      } else {
        console.error('FloatingChat: Authentication failed:', loginResult.message);
        return false;
      }
    } catch (error) {
      console.error('FloatingChat: Authentication error:', error);
      return false;
    }
  }

  /**
   * 设置外部变量
   */
  private async setParameters(parameters: any): Promise<boolean> {
    if (!this.configManager) {
      console.error('FloatingChat: ConfigManager not initialized');
      return false;
    }

    const config = this.configManager.getConfig();
    
    try {
      const baseUrl = config.baseUrl || '';
      const token = config.token;
      
      if (!token) {
        console.warn('FloatingChat: No token available for setting parameters');
        return false;
      }

      await setExternalVariables(baseUrl, parameters, token);
      console.log('FloatingChat: Parameters set successfully');
      return true;
    } catch (error) {
      console.error('FloatingChat: Failed to set parameters:', error);
      return false;
    }
  }

  /**
   * 初始化浮动聊天组件
   */
  public async init(config: FloatingChatConfig = {}): Promise<boolean> {
    try {
      // 如果已初始化，先销毁
      if (this.isInitialized) {
        console.warn('FloatingChat: Already initialized, destroying previous instance');
        this.destroy();
      }

      // 配置Token管理器存储类型和Key
      const storageType = config.storageType || 'sessionStorage';
      tokenManager.setStorageType(storageType, 'smart-agent-sdk-token');
      
      // 每次 init 都清空之前的 token，确保刷新后重新登录
      tokenManager.removeToken();
      console.log('FloatingChat: Cleared previous token');

      // 创建配置管理器
      this.configManager = new ConfigManager(config);
      const internalConfig = this.configManager.getConfig();

      // 创建存储管理器
      this.storageManager = new StorageManager(internalConfig.saveSession);

      // 创建事件管理器
      this.eventManager = new EventManager(internalConfig.baseUrl);

      // 注入样式
      injectStyles(WIDGET_STYLES, STYLE_ID);

      // 创建容器
      this.container = document.createElement('div');
      this.container.className = 'floating-chat-widget';
      document.body.appendChild(this.container);

      // 创建按钮
      this.button = new FloatingButton(internalConfig, this.handleButtonClick.bind(this));
      const buttonElement = this.button.create();
      this.container.appendChild(buttonElement);

      // 创建窗口
      this.window = new ChatWindow(
        internalConfig,
        this.handleMinimize.bind(this),
        this.handleClose.bind(this)
      );
      // 注册 iframe 加载完成回调，替代 setTimeout 竞态
      this.window.setOnIframeLoad((iframe) => {
        if (this.eventManager) {
          this.eventManager.setIframe(iframe);
        }
      });
      const windowElement = this.window.create();
      this.container.appendChild(windowElement);

      // 设置事件监听
      this.setupEventListeners();

      // 恢复未读消息计数
      const unreadCount = this.storageManager.getUnreadCount();
      if (unreadCount > 0) {
        this.button.setUnreadCount(unreadCount);
      }

      this.isInitialized = true;

      // 如果配置了自动打开
      if (internalConfig.showOnLoad) {
        setTimeout(() => this.open(), 100);
      }

      // 触发ready回调
      if (internalConfig.onReady) {
        internalConfig.onReady();
      }
      this.eventManager.emit('ready');

      console.log('FloatingChat: Initialized successfully');
      return true;
    } catch (error) {
      console.error('FloatingChat: Initialization failed', error);
      this.destroy();
      return false;
    }
  }

  private setupEventListeners(): void {
    if (!this.eventManager || !this.configManager) return;

    const config = this.configManager.getConfig();

    // 监听iframe消息
    this.eventManager.on('message', (data: any) => {
      if (config.onMessage) {
        config.onMessage(data);
      }
    });

    this.eventManager.on('error', (data: any) => {
      if (config.onError) {
        config.onError(data);
      }
    });

    this.eventManager.on('unread-update', (data: any) => {
      if (data && typeof data.count === 'number') {
        this.button?.setUnreadCount(data.count);
        this.storageManager?.saveUnreadCount(data.count);
      }
    });
  }

  private handleButtonClick(): void {
    // 异步调用但不阻塞
    this.toggle()
  }

  private handleMinimize(): void {
    this.close();
  }

  private handleClose(): void {
    this.close();
  }

  /**
   * 打开聊天窗口
   */
  public async open(): Promise<void> {
    if (!this.isInitialized || !this.window || !this.button) {
      console.warn('FloatingChat: Not initialized');
      return;
    }

    if (this.window.isOpen()) {
      return;
    }

    // 立即打开窗口（显示 loading 状态）
    this.window.open();
    this.button.hide();
    this.button.setUnreadCount(0);

    // 在后台进行认证
    const authSuccess = await this.authenticate();
    if (!authSuccess) {
      console.error('FloatingChat: Authentication failed');
      // 显示认证失败的错误信息
      this.window.showAuthError('认证失败，无法加载聊天窗口');
      // 触发错误事件
      this.eventManager?.emit('error', { 
        message: '认证失败，无法打开聊天窗口',
        code: 'AUTH_FAILED'
      });
      return;
    }

    // 认证成功，加载 iframe（iframe onload 时自动设置 eventManager 引用）
    this.window.loadIframe();

    // 保存状态
    this.storageManager?.saveWindowState(true);
    this.storageManager?.saveUnreadCount(0);

    // 触发回调
    const config = this.configManager?.getConfig();
    if (config?.onOpen) {
      config.onOpen();
    }
    this.eventManager?.emit('open');

    // 添加点击外部关闭监听
    setTimeout(() => {
      document.addEventListener('mousedown', this.handleDocumentClick);
    }, 0);
  }

  /**
   * 关闭聊天窗口
   */
  public close(): void {
    if (!this.isInitialized || !this.window || !this.button) {
      console.warn('FloatingChat: Not initialized');
      return;
    }

    if (!this.window.isOpen()) {
      return;
    }

    this.window.close();
    this.button.setIcon(false);
    
    // 只有在配置允许显示按钮时才显示
    const config = this.configManager?.getConfig();
    if (config?.showButton !== false) {
      this.button.show();
    }

    // 保存状态
    this.storageManager?.saveWindowState(false);

    // 触发回调
    if (config?.onClose) {
      config.onClose();
    }
    this.eventManager?.emit('close');

    // 移除点击外部关闭监听
    document.removeEventListener('mousedown', this.handleDocumentClick);
  }

  /**
   * 切换窗口状态
   */
  public toggle(): void {
    if (this.window?.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * 发送消息到聊天窗口
   */
  public sendMessage(text: string): void {
    if (!this.isInitialized || !this.eventManager) {
      console.warn('FloatingChat: Not initialized');
      return;
    }

    this.eventManager.sendToIframe('external-message', { message: text });
  }

  /**
   * 直接调用智能体对话接口（绕过UI）
   * @param agentId 智能体ID
   * @param message 消息内容 // 仅支持文字输入
   * @param onUpdate 可选：流式更新回调，参数为(内容, 思考过程)
   * @param useSdkUI 可选：是否由SDK渲染全屏居中弹窗展示流式对话
   * @returns Promise<string> 最终完整回复内容
   */
  public async chat(
    agentId: string,
    message: string,
    onUpdate?: (content: string, reasoning?: string) => void,
    useSdkUI?: boolean
  ): Promise<string> {
    if (!this.isInitialized) {
      console.warn('FloatingChat: Not initialized');
      throw new Error('MsebcChat未初始化');
    }

    // useSdkUI 模式：先打开弹窗，再在后台进行认证、建会话、流式对话
    if (useSdkUI) {
      return this.chatWithUI(agentId, message);
    }

    // 调用 chat 前先进行认证
    const authSuccess = await this.authenticate();
    if (!authSuccess) {
      console.error('FloatingChat: Authentication required before chat');
      throw new Error('认证失败，无法进行对话');
    }

    const config = this.configManager?.getConfig();
    const baseUrl = config?.baseUrl || '';
    const token = config?.token;

    if (!token) {
      console.warn('FloatingChat: No token available after authentication');
      throw new Error('认证后仍无法获取Token');
    }

    // 生成新的 sessionId
    const sessionId = `sdk_chat_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

    try {
      // 1. 创建会话
      await createSession(baseUrl, token, {
        id: sessionId,
        agentId: agentId,
        title: message.substring(0, 50) || '新对话'
      });

      // 用户消息由后端在 POST /stream 中统一入库，前端不再重复保存
    } catch (error) {
      console.error('SDK: Failed to initialize chat session', error);
      throw error;
    }

    const formatMsg = JSON.stringify([{type:'text',text:message}]);

    return new Promise((resolve, reject) => {
      let fullContent = '';

      sendMessageToAgent(
        baseUrl,
        token,
        agentId,
        formatMsg,
        sessionId,
        (content, responseType, messageId, reasoning) => {
          if (content) {
            fullContent = content;
          }
          if (onUpdate) {
            onUpdate(content, reasoning);
          }
        },
        () => {
          resolve(fullContent);
        },
        (error) => {
          console.error('SDK Chat Error:', error);
          reject(error);
        },
        undefined, // signal
        message,   // displayContent：用户消息展示文本
      );
    });
  }

  /**
   * useSdkUI 模式：先打开弹窗，再在后台完成认证、建会话、流式对话
   */
  private chatWithUI(agentId: string, message: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const config = this.configManager!.getConfig();
      let modal: ChatModal | null = null;
      let resolved = false;

      const done = (result: string) => {
        if (resolved) return;
        resolved = true;
        resolve(result);
      };

      const fail = (err: Error) => {
        if (resolved) return;
        resolved = true;
        if (modal) {
          modal.showError(`对话失败: ${err.message}`);
        }
        reject(err);
      };

      // 1. 立即创建并显示弹窗（先渲染 UI）
      modal = new ChatModal(
        {
          title: config.title || 'AI 对话',
          theme: config.theme,
          primaryColor: config.primaryColor,
        },
        () => {
          fail(new Error('用户关闭了对话窗口'));
        }
      );
      modal.create();
      modal.setUserMessage(message);
      modal.show();

      // 2. 后台异步执行：认证 → 建会话 → 存消息 → 流式对话
      this.runChatFlow(agentId, message, modal, done, fail);
    });
  }

  /**
   * 后台执行完整的对话流程：认证、建会话、存消息、流式对话
   */
  private async runChatFlow(
    agentId: string,
    message: string,
    modal: ChatModal,
    done: (result: string) => void,
    fail: (err: Error) => void
  ): Promise<void> {
    try {
      const authSuccess = await this.authenticate();
      if (!authSuccess) {
        fail(new Error('认证失败，无法进行对话'));
        return;
      }

      const config = this.configManager?.getConfig();
      const baseUrl = config?.baseUrl || '';
      const token = config?.token;

      if (!token) {
        fail(new Error('认证后仍无法获取Token'));
        return;
      }

      const sessionId = `sdk_chat_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

      const abortController = new AbortController();

      // 注册取消回调：关闭弹窗时中断正在进行的流式对话
      modal.setCancelFn(() => {
        abortController.abort();
      });

      await createSession(baseUrl, token, {
        id: sessionId,
        agentId: agentId,
        title: message.substring(0, 50) || '新对话'
      });

      // 用户消息由后端在 POST /stream 中统一入库，前端不再重复保存

      const formatMsg = JSON.stringify([{type:'text',text:message}]);
      let fullContent = '';

      sendMessageToAgent(
        baseUrl,
        token,
        agentId,
        formatMsg,
        sessionId,
        (content, _responseType, _messageId, reasoning, isComplete) => {
          // isComplete → 权威全量替换，否则 → 增量追加
          if (content) {
            if (isComplete) {
              fullContent = content;
            } else {
              fullContent += content;
            }
          }
          modal.appendContent(content, reasoning, isComplete);
        },
        () => {
          done(fullContent);
        },
        (error) => {
          console.error('SDK Chat Error:', error);
          fail(error);
        },
        abortController.signal,
        message, // displayContent：用户消息展示文本
      );
    } catch (error) {
      fail(error as Error);
    }
  }

  /**
   * 更新配置
   */
  public updateConfig(newConfig: Partial<FloatingChatConfig>): void {
    if (!this.isInitialized || !this.configManager) {
      console.warn('FloatingChat: Not initialized');
      return;
    }

    const oldConfig = this.configManager.getConfig();
    this.configManager.updateConfig(newConfig);
    const config = this.configManager.getConfig();

    // 需要全量重建的核心变更
    const needsReinit = newConfig.baseUrl !== undefined && newConfig.baseUrl !== oldConfig.baseUrl;
    const needsStorageChange = newConfig.storageType !== undefined && newConfig.storageType !== oldConfig.storageType;

    if (needsReinit || needsStorageChange) {
      this.init(config);
      return;
    }

    // 增量应用可视化配置变更，避免销毁重建
    if (this.window) {
      if (newConfig.theme !== undefined) {
        this.window.updateTheme(newConfig.theme);
      }
      if (newConfig.title !== undefined) {
        const titleEl = this.window.getElement()?.querySelector('.window-title');
        if (titleEl) titleEl.textContent = newConfig.title;
      }
      if (newConfig.fullScreen !== undefined) {
        const el = this.window.getElement();
        const btn = el?.querySelector('.btn-fullscreen') as HTMLButtonElement | null;
        if (el) {
          const isCurrentlyFullscreen = el.classList.contains('is-fullscreen');
          if (newConfig.fullScreen && !isCurrentlyFullscreen) {
            el.classList.add('is-fullscreen');
            document.body.classList.add('floating-chat-fullscreen-mode');
            if (btn) {
              btn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path></svg>';
              btn.title = '退出全屏';
            }
          } else if (!newConfig.fullScreen && isCurrentlyFullscreen) {
            el.classList.remove('is-fullscreen');
            document.body.classList.remove('floating-chat-fullscreen-mode');
            if (btn) {
              btn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg>';
              btn.title = '全屏';
            }
          }
        }
      }

      // 如果变更影响 embed URL，刷新 iframe
      const embedUrlChanged =
        newConfig.agentId !== undefined ||
        newConfig.placeholder !== undefined ||
        newConfig.showBranding !== undefined ||
        newConfig.welcomeMessage !== undefined ||
        newConfig.loginInfo !== undefined ||
        newConfig.parameters !== undefined;

      if (embedUrlChanged && this.window.isOpen()) {
        this.window.refreshIframe();
      }
    }

    // 应用按钮可视化变更
    if (this.button) {
      if (newConfig.showButton !== undefined) {
        if (newConfig.showButton) {
          this.button.show();
        } else {
          this.button.hide();
        }
      }
      const btnEl = this.button.getElement();
      if (btnEl) {
        if (newConfig.primaryColor !== undefined) {
          const iconEl = btnEl.querySelector('.button-icon') as HTMLElement | null;
          if (iconEl) iconEl.style.backgroundColor = newConfig.primaryColor;
        }
        if (newConfig.buttonSize !== undefined) {
          btnEl.style.width = `${newConfig.buttonSize}px`;
          btnEl.style.height = `${newConfig.buttonSize}px`;
        }
      }
    }
  }

  /**
   * 显示按钮
   */
  public showButton(): void {
    this.button?.show();
  }

  /**
   * 隐藏按钮
   */
  public hideButton(): void {
    this.button?.hide();
  }

  /**
   * 检查窗口是否打开
   */
  public isOpen(): boolean {
    return this.window?.isOpen() ?? false;
  }

  /**
   * 获取未读消息数
   */
  public getUnreadCount(): number {
    return this.storageManager?.getUnreadCount() ?? 0;
  }

  /**
   * 获取当前配置
   */
  public getConfig(): FloatingChatConfig {
    return this.configManager?.getConfig() ?? {};
  }

  /**
   * 监听事件
   */
  public on(event: EventType, callback: Function): void {
    if (!this.eventManager) {
      console.warn('FloatingChat: Not initialized');
      return;
    }
    this.eventManager.on(event, callback);
  }

  /**
   * 移除事件监听
   */
  public off(event: EventType, callback: Function): void {
    if (!this.eventManager) {
      console.warn('FloatingChat: Not initialized');
      return;
    }
    this.eventManager.off(event, callback);
  }

  /**
   * 销毁组件
   */
  public destroy(): void {
    // 销毁各个组件
    this.button?.destroy();
    this.window?.destroy();
    this.eventManager?.destroy();

    // 移除点击外部关闭监听
    document.removeEventListener('mousedown', this.handleDocumentClick);

    // 移除容器
    if (this.container) {
      this.container.remove();
      this.container = null;
    }

    // 移除样式
    removeStyles(STYLE_ID);

    // 清空引用
    this.button = null;
    this.window = null;
    this.eventManager = null;
    this.configManager = null;
    this.storageManager = null;
    this.isInitialized = false;
    this.isAuthenticated = false;

    console.log('FloatingChat: Destroyed');
  }
}

// 创建全局单例
const floatingChatWidget = new FloatingChatWidget();

// 导出到全局
declare global {
  interface Window {
    MsebcChat: typeof floatingChatWidget;
  }
}

if (typeof window !== 'undefined') {
  window.MsebcChat = floatingChatWidget;
}

// 导出类型和实例
export default floatingChatWidget;
export type { FloatingChatConfig, EventType, Position, Theme };
